import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
interface CostSectionProps {
  goToSlide?: (index: number) => void;
}
export function CostSection({
  goToSlide
}: CostSectionProps) {
  const values = ['No changes to AEM core', 'Analytics preserved (Adobe + Google)', 'Measurable results from Phase 1', 'Scalable to all markets'];
  return <section className="h-full flex flex-col justify-between py-8 px-4 sm:px-6 lg:px-8 bg-white overflow-y-auto lg:overflow-hidden" data-id="element-55">
      <div className="max-w-6xl mx-auto w-full flex-grow flex flex-col justify-center" data-id="element-56">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="mb-8 text-center" data-id="element-57">
          <span className="text-xs font-bold tracking-widest text-[#80BC41] uppercase mb-3 block" data-id="element-58">
            06 — Investment
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] font-heading" data-id="element-59">
            Clear pricing. Measurable outcomes.
          </h2>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-5 mb-8" data-id="element-60">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1,
          ease: 'easeOut'
        }} className="bg-white p-6 rounded-xl border-2 border-[#EB0A1E]/15 shadow-md relative flex flex-col" data-id="element-61">
            <div className="absolute -top-2.5 left-5" data-id="element-62">
              <span className="bg-[#EB0A1E] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-63">
                Phase 1
              </span>
            </div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-0.5 mt-1" data-id="element-64">
              Test & Validate
            </h3>
            <p className="text-[10px] text-gray-400 font-medium mb-3" data-id="element-65">
              Frontend only, 1 landing page
            </p>
            <div className="text-2xl font-bold text-[#1a1a1a] mb-3 font-heading" data-id="element-66">
              ~1,500 €
            </div>
            <p className="text-gray-500 text-xs flex-grow" data-id="element-67">
              Direct comparison against existing page.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.2,
          ease: 'easeOut'
        }} className="bg-white p-6 rounded-xl border border-[#80BC41]/20 shadow-sm flex flex-col relative" data-id="element-68">
            <div className="absolute -top-2.5 left-5" data-id="element-69">
              <span className="bg-[#80BC41] text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-70">
                Phase 2
              </span>
            </div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-0.5 mt-1" data-id="element-71">
              CMS & Scale
            </h3>
            <p className="text-[10px] text-gray-400 font-medium mb-3" data-id="element-72">
              October CMS for all offers
            </p>
            <div className="text-2xl font-bold text-[#1a1a1a] mb-3 font-heading" data-id="element-73">
              ~3,000 €
            </div>
            <p className="text-gray-500 text-xs flex-grow" data-id="element-74">
              Centralized management across all offers and languages.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.3,
          ease: 'easeOut'
        }} className="bg-gray-50 p-6 rounded-xl border border-gray-100 flex flex-col relative" data-id="element-75">
            <div className="absolute -top-2.5 left-5" data-id="element-76">
              <span className="bg-gray-500 text-white text-[10px] font-bold px-2.5 py-1 rounded-full uppercase tracking-wider" data-id="element-77">
                Phase 3
              </span>
            </div>
            <h3 className="text-base font-bold text-[#1a1a1a] mb-0.5 mt-1" data-id="element-78">
              Maintenance
            </h3>
            <p className="text-[10px] text-gray-400 font-medium mb-3" data-id="element-79">
              Hosting, CDN, updates
            </p>
            <div className="text-2xl font-bold text-[#1a1a1a] mb-3 font-heading" data-id="element-80">
              ~125 €{' '}
              <span className="text-xs font-medium text-gray-400" data-id="element-81">/ month</span>
            </div>
            <p className="text-gray-500 text-xs flex-grow" data-id="element-82">
              Ongoing infrastructure and system maintenance for all markets.
            </p>
          </motion.div>
        </div>

        <motion.p initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        duration: 0.5,
        delay: 0.35,
        ease: 'easeOut'
      }} className="text-[11px] text-gray-400 text-center mb-6 italic" data-id="element-83">
          Includes content entry for one landing page (one language). Additional
          content entry is not included and will be quoted separately.
        </motion.p>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.4,
        ease: 'easeOut'
      }} className="max-w-4xl mx-auto bg-gray-50 rounded-2xl p-8 border border-gray-100 mb-8 w-full" data-id="element-84">
          <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-5" data-id="element-85">
            {values.map((value, idx) => <div key={idx} className="flex items-start gap-3" data-id="element-86">
                <div className="w-6 h-6 rounded-full bg-[#80BC41]/10 flex items-center justify-center shrink-0 mt-0.5" data-id="element-87">
                  <Check className="w-3.5 h-3.5 text-[#80BC41]" data-id="element-88" />
                </div>
                <span className="text-gray-700 font-semibold text-sm leading-snug" data-id="element-89">
                  {value}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>

      <div className="mt-6 pt-4 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-gray-400 font-medium" data-id="element-90">
        <div className="flex items-center gap-3" data-id="element-91">
          <img src="https://cdn.magicpatterns.com/uploads/5RkwGoMRntLb6F5ZXgtHdC/toyota-logo.svg" alt="Toyota" className="h-5 opacity-40" data-id="element-92" />
          <div className="w-px h-3 bg-[#80BC41]/20" data-id="element-93"></div>
          <img src="https://cdn.magicpatterns.com/uploads/7Sj6JSkedxavvMrCD56G1H/epic-logo-green.svg" alt="Agency Epic" className="h-5 opacity-50" data-id="element-94" />
        </div>
        <span data-id="element-95">&copy; {new Date().getFullYear()} Agency Epic</span>
      </div>
    </section>;
}